import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import moment, { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faCalendar, faClock } from '@fortawesome/pro-regular-svg-icons';
import useCustomTheme from '../../hooks/theme-hook';
import useTranslation from '../../hooks/translation-hook';
import { useAppSelector } from '../../hooks/store-hook';
import Label from './Label';
import CustomButton from './CustomButton';
import FieldErrorMessage from './FieldErrorMessage';

type PickerMode = 'date' | 'time';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';
const DATE_INPUT_FORMATS = [DATE_FORMAT, 'YYYY/MM/DD'];
const TIME_INPUT_FORMATS = [TIME_FORMAT, 'h:mm A'];

export interface UnifiedDateTimePickerProps {
  value?: string;
  mode: PickerMode;
  onChange(value: string): void;
  placeholder?: string;
  accessibilityLabel?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  showErrorMessage?: boolean;
  errorTextStyle?: StyleProp<TextStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  fieldStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  valueTextColor?: string;
  placeholderTextColor?: string;
  iconColor?: string;
  onOpen?(): void;
}

interface PickerColumnItem {
  label: string;
  value: number;
  disabled?: boolean;
}

interface PickerColumnProps {
  items: PickerColumnItem[];
  selectedValue: number;
  isVisible: boolean;
  onSelect(value: number): void;
}

const PICKER_ROW_HEIGHT = 38;
const PICKER_SCROLL_TOP_PADDING_ROWS = 2;

const PickerColumn = (props: PickerColumnProps) => {
  const theme = useCustomTheme();
  const scrollViewRef = useRef<ScrollView>(null);

  useEffect(() => {
    if (!props.isVisible) {
      return;
    }

    const selectedIndex = props.items.findIndex((item) => item.value === props.selectedValue);
    if (selectedIndex < 0) {
      return;
    }

    const scrollOffset = Math.max(0, selectedIndex * PICKER_ROW_HEIGHT - PICKER_ROW_HEIGHT * PICKER_SCROLL_TOP_PADDING_ROWS);
    const timerId = setTimeout(() => {
      scrollViewRef.current?.scrollTo({ y: scrollOffset, animated: false });
    }, 0);

    return () => clearTimeout(timerId);
  }, [props.isVisible, props.items, props.selectedValue]);

  return (
    <View style={{ flex: 1, borderWidth: 1, borderColor: theme.borderColorLight?.toString() ?? '#d7e3ec', borderRadius: 8, overflow: 'hidden' }}>
      <ScrollView ref={scrollViewRef} showsVerticalScrollIndicator>
        {props.items.map((item) => {
          const selected = item.value === props.selectedValue;
          const rowBackgroundColor = item.disabled
            ? selected
              ? theme.background.secondary?.toString() ?? '#344450'
              : theme.background.primaryLight?.toString() ?? '#ffffff'
            : selected
              ? theme.background.secondary?.toString() ?? '#e8f4fc'
              : theme.background.primary?.toString() ?? '#ffffff';
          const rowTextColor = item.disabled
            ? theme.borderColor?.toString() ?? '#6b93b5'
            : selected
              ? theme.text.darkTurquoiseTwo?.toString() ?? '#1f78c8'
              : theme.text.primary?.toString() ?? '#102334';
          return (
            <Pressable
              key={`${item.value}`}
              disabled={item.disabled}
              onPress={() => props.onSelect(item.value)}
              style={{
                minHeight: 38,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: rowBackgroundColor,
              }}
            >
              <Label style={{ fontSize: 15, color: rowTextColor, fontWeight: selected ? '700' : '400' }}>{item.label}</Label>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
};

const parseIncomingValue = (mode: PickerMode, value?: string): Moment | undefined => {
  if (!value?.trim()) {
    return undefined;
  }

  const parsed =
    mode === 'date' ? moment(value.trim(), DATE_INPUT_FORMATS, true) : moment(value.trim(), TIME_INPUT_FORMATS, true);

  return parsed.isValid() ? parsed : undefined;
};

const clampDate = (date: Moment, minDate?: Moment, maxDate?: Moment): Moment => {
  let next = date.clone().startOf('day');
  if (minDate && next.isBefore(minDate, 'day')) {
    next = minDate.clone().startOf('day');
  }
  if (maxDate && next.isAfter(maxDate, 'day')) {
    next = maxDate.clone().startOf('day');
  }
  return next;
};

const clampTime = (date: Moment, minMinutes: number, maxMinutes: number): Moment => {
  const currentMinutes = date.hour() * 60 + date.minute();
  const boundedMinutes = Math.max(minMinutes, Math.min(maxMinutes, currentMinutes));
  return date
    .clone()
    .hour(Math.floor(boundedMinutes / 60))
    .minute(boundedMinutes % 60)
    .second(0)
    .millisecond(0);
};

const formatValue = (mode: PickerMode, value?: string): string | undefined => {
  const parsed = parseIncomingValue(mode, value);
  if (!parsed) {
    return undefined;
  }
  return parsed.format(mode === 'date' ? DATE_FORMAT : TIME_FORMAT);
};

const UnifiedDateTimePicker = (props: UnifiedDateTimePickerProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const errorBorderColor = theme.isDark ? (theme.text.error?.toString() ?? '#ff9bb4') : (theme.colorErrorPrimary?.toString() ?? '#8e183d');
  const showErrorState = !!props.hasError || !!props.errorMessage;
  const userLanguage = useAppSelector((selector) => selector.userPrefs.uiLanguage);
  const [isVisible, setIsVisible] = useState(false);
  const isFrench = (userLanguage ?? '').toLowerCase().startsWith('fr');

  const minDateMoment = useMemo(() => (props.minDate ? moment(props.minDate).startOf('day') : undefined), [props.minDate]);
  const maxDateMoment = useMemo(() => (props.maxDate ? moment(props.maxDate).startOf('day') : undefined), [props.maxDate]);
  const minTimeMinutes = useMemo(() => {
    if (!props.minDate) {
      return 0;
    }
    const parsed = moment(props.minDate);
    return parsed.hour() * 60 + parsed.minute();
  }, [props.minDate]);
  const maxTimeMinutes = useMemo(() => {
    if (!props.maxDate) {
      return 23 * 60 + 59;
    }
    const parsed = moment(props.maxDate);
    return parsed.hour() * 60 + parsed.minute();
  }, [props.maxDate]);

  const normalizedValue = useMemo(() => formatValue(props.mode, props.value), [props.mode, props.value]);

  const buildInitialDraft = () => {
    const parsed = parseIncomingValue(props.mode, props.value);

    if (props.mode === 'date') {
      const fallback = moment().startOf('day');
      return clampDate(parsed ?? fallback, minDateMoment, maxDateMoment);
    }

    const fallback = moment().second(0).millisecond(0);
    const boundedMin = Math.max(0, Math.min(23 * 60 + 59, minTimeMinutes));
    const boundedMax = Math.max(boundedMin, Math.min(23 * 60 + 59, maxTimeMinutes));
    return clampTime(parsed ?? fallback, boundedMin, boundedMax);
  };

  const [draftValue, setDraftValue] = useState<Moment>(buildInitialDraft);

  const openPicker = () => {
    if (props.disabled) {
      return;
    }

    setDraftValue(buildInitialDraft());
    props.onOpen && props.onOpen();
    setIsVisible(true);
  };

  const closePicker = () => {
    setIsVisible(false);
  };

  const confirmSelection = () => {
    const nextValue = draftValue.format(props.mode === 'date' ? DATE_FORMAT : TIME_FORMAT);
    props.onChange(nextValue);
    setIsVisible(false);
  };

  const isDateAllowed = (year: number, month: number, day: number): boolean => {
    const candidate = moment({ year, month: month - 1, day }).startOf('day');
    if (!candidate.isValid()) {
      return false;
    }
    if (minDateMoment && candidate.isBefore(minDateMoment, 'day')) {
      return false;
    }
    if (maxDateMoment && candidate.isAfter(maxDateMoment, 'day')) {
      return false;
    }
    return true;
  };

  const updateDatePart = (part: 'year' | 'month' | 'day', value: number) => {
    const currentYear = draftValue.year();
    const currentMonth = draftValue.month() + 1;
    const currentDay = draftValue.date();

    const nextYear = part === 'year' ? value : currentYear;
    const nextMonth = part === 'month' ? value : currentMonth;
    const maxDayInMonth = moment({ year: nextYear, month: nextMonth - 1, day: 1 }).daysInMonth();
    const desiredDay = part === 'day' ? value : Math.min(currentDay, maxDayInMonth);

    const unclamped = moment({ year: nextYear, month: nextMonth - 1, day: desiredDay });
    const clamped = clampDate(unclamped, minDateMoment, maxDateMoment);
    setDraftValue(clamped);
  };

  const updateTimePart = (part: 'hour' | 'minute', value: number) => {
    const candidate = draftValue.clone();
    if (part === 'hour') {
      candidate.hour(value);
    } else {
      candidate.minute(value);
    }

    const boundedMin = Math.max(0, Math.min(23 * 60 + 59, minTimeMinutes));
    const boundedMax = Math.max(boundedMin, Math.min(23 * 60 + 59, maxTimeMinutes));
    setDraftValue(clampTime(candidate, boundedMin, boundedMax));
  };

  const yearOptions = useMemo(() => {
    if (props.mode !== 'date') {
      return [];
    }
    const startYear = minDateMoment?.year() ?? 1900;
    const endYear = maxDateMoment?.year() ?? 2100;

    const items: PickerColumnItem[] = [];
    for (let year = startYear; year <= endYear; year++) {
      items.push({ value: year, label: `${year}` });
    }
    return items;
  }, [props.mode, minDateMoment, maxDateMoment]);

  const monthOptions = useMemo(() => {
    if (props.mode !== 'date') {
      return [];
    }

    const year = draftValue.year();
    const items: PickerColumnItem[] = [];
    for (let month = 1; month <= 12; month++) {
      const maxDay = moment({ year, month: month - 1, day: 1 }).daysInMonth();
      const day = Math.min(draftValue.date(), maxDay);
      items.push({
        value: month,
        label: `${month}`.padStart(2, '0'),
        disabled: !isDateAllowed(year, month, day),
      });
    }

    return items;
  }, [props.mode, draftValue, minDateMoment, maxDateMoment]);

  const dayOptions = useMemo(() => {
    if (props.mode !== 'date') {
      return [];
    }

    const year = draftValue.year();
    const month = draftValue.month() + 1;
    const maxDay = draftValue.daysInMonth();

    const items: PickerColumnItem[] = [];
    for (let day = 1; day <= maxDay; day++) {
      items.push({
        value: day,
        label: `${day}`.padStart(2, '0'),
        disabled: !isDateAllowed(year, month, day),
      });
    }

    return items;
  }, [props.mode, draftValue, minDateMoment, maxDateMoment]);

  const hourOptions = useMemo(() => {
    if (props.mode !== 'time') {
      return [];
    }

    const minute = draftValue.minute();
    const minBound = Math.max(0, Math.min(23 * 60 + 59, minTimeMinutes));
    const maxBound = Math.max(minBound, Math.min(23 * 60 + 59, maxTimeMinutes));

    const items: PickerColumnItem[] = [];
    for (let hour = 0; hour <= 23; hour++) {
      const total = hour * 60 + minute;
      items.push({
        value: hour,
        label: `${hour}`.padStart(2, '0'),
        disabled: total < minBound || total > maxBound,
      });
    }

    return items;
  }, [props.mode, draftValue, minTimeMinutes, maxTimeMinutes]);

  const minuteOptions = useMemo(() => {
    if (props.mode !== 'time') {
      return [];
    }

    const hour = draftValue.hour();
    const minBound = Math.max(0, Math.min(23 * 60 + 59, minTimeMinutes));
    const maxBound = Math.max(minBound, Math.min(23 * 60 + 59, maxTimeMinutes));

    const items: PickerColumnItem[] = [];
    for (let minute = 0; minute <= 59; minute++) {
      const total = hour * 60 + minute;
      items.push({
        value: minute,
        label: `${minute}`.padStart(2, '0'),
        disabled: total < minBound || total > maxBound,
      });
    }

    return items;
  }, [props.mode, draftValue, minTimeMinutes, maxTimeMinutes]);

  const dateColumnLabels = useMemo(
    () =>
      isFrench
        ? {
            year: 'Année',
            month: 'Mois',
            day: 'Date',
          }
        : {
            year: 'Year',
            month: 'Month',
            day: 'Date',
          },
    [isFrench],
  );
  const timeColumnLabels = useMemo(
    () =>
      isFrench
        ? {
            hour: 'Heure',
            minute: 'Minutes',
          }
        : {
            hour: 'Hour',
            minute: 'Minutes',
          },
    [isFrench],
  );

  return (
    <View style={props.containerStyle}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={props.accessibilityLabel ?? props.placeholder}
        accessibilityValue={{ text: normalizedValue ?? '' }}
        disabled={props.disabled}
        onPress={openPicker}
        style={[
          {
            borderWidth: showErrorState && theme.isDark ? 1.5 : 1,
            borderColor: showErrorState ? errorBorderColor : theme.borderColorLight?.toString(),
            borderRadius: 8,
            minHeight: 46,
            paddingHorizontal: 12,
            backgroundColor: theme.background.primary?.toString(),
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            opacity: props.disabled ? 0.5 : 1,
          },
          props.fieldStyle,
        ]}
      >
        <Label
          style={[
            {
              fontSize: 16,
              color: normalizedValue
                ? props.valueTextColor ?? theme.text.primary
                : props.placeholderTextColor ?? theme.inputTextPlaceholder,
            },
            props.textStyle,
          ]}
        >
          {normalizedValue || props.placeholder || ''}
        </Label>
        <FontAwesomeIcon
          icon={props.mode === 'date' ? faCalendar : faClock}
          size={18}
          color={props.iconColor ?? (theme.text.darkTurquoiseTwo ?? theme.text.primary).toString()}
        />
      </Pressable>
      {props.showErrorMessage && !!props.errorMessage && <FieldErrorMessage message={props.errorMessage} style={props.errorTextStyle} />}

      <Modal visible={isVisible} transparent animationType="fade" onRequestClose={closePicker}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 16 }}>
          <Pressable
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.35)' }}
            onPress={closePicker}
          />

          <View
            style={{
              width: '100%',
              maxWidth: 420,
              borderRadius: 12,
              backgroundColor: theme.background.modal,
              padding: 16,
              gap: 12,
            }}
          >
            <Label style={{ fontSize: 18, fontWeight: '700', color: theme.text.titleDark }}>
              {props.mode === 'date' ? t('airport-booking-date') || 'Date' : t('airport-booking-time') || 'Time'}
            </Label>

            {props.mode === 'date' && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}>
                  <Label style={{ fontSize: 13, fontWeight: '600', color: theme.text.primary, textAlign: 'center' }}>{dateColumnLabels.year}</Label>
                </View>
                <View style={{ flex: 1 }}>
                  <Label style={{ fontSize: 13, fontWeight: '600', color: theme.text.primary, textAlign: 'center' }}>{dateColumnLabels.month}</Label>
                </View>
                <View style={{ flex: 1 }}>
                  <Label style={{ fontSize: 13, fontWeight: '600', color: theme.text.primary, textAlign: 'center' }}>{dateColumnLabels.day}</Label>
                </View>
              </View>
            )}
            {props.mode === 'time' && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}>
                  <Label style={{ fontSize: 13, fontWeight: '600', color: theme.text.primary, textAlign: 'center' }}>{timeColumnLabels.hour}</Label>
                </View>
                <View style={{ flex: 1 }}>
                  <Label style={{ fontSize: 13, fontWeight: '600', color: theme.text.primary, textAlign: 'center' }}>{timeColumnLabels.minute}</Label>
                </View>
              </View>
            )}

            <View style={{ flexDirection: 'row', gap: 8, maxHeight: 220 }}>
              {props.mode === 'date' ? (
                <>
                  <PickerColumn
                    items={yearOptions}
                    selectedValue={draftValue.year()}
                    isVisible={isVisible}
                    onSelect={(value) => updateDatePart('year', value)}
                  />
                  <PickerColumn
                    items={monthOptions}
                    selectedValue={draftValue.month() + 1}
                    isVisible={isVisible}
                    onSelect={(value) => updateDatePart('month', value)}
                  />
                  <PickerColumn
                    items={dayOptions}
                    selectedValue={draftValue.date()}
                    isVisible={isVisible}
                    onSelect={(value) => updateDatePart('day', value)}
                  />
                </>
              ) : (
                <>
                  <PickerColumn
                    items={hourOptions}
                    selectedValue={draftValue.hour()}
                    isVisible={isVisible}
                    onSelect={(value) => updateTimePart('hour', value)}
                  />
                  <PickerColumn
                    items={minuteOptions}
                    selectedValue={draftValue.minute()}
                    isVisible={isVisible}
                    onSelect={(value) => updateTimePart('minute', value)}
                  />
                </>
              )}
            </View>

            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
              <CustomButton
                title={t('cancel')}
                secondary
                style={{ minWidth: 120, minHeight: 48 } as any}
                textStyle={{ paddingVertical: 0, lineHeight: 20, fontSize: 16 }}
                onPress={closePicker}
              />
              <CustomButton
                title={t('confirm')}
                style={{ minWidth: 120, minHeight: 48 } as any}
                textStyle={{ paddingVertical: 0, lineHeight: 20, fontSize: 16 }}
                onPress={confirmSelection}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default UnifiedDateTimePicker;
